export const ROOT_ID = 'root';

const DEFAULT_CONFIG = { 
    isLeafKey: 'isleaf', 
    idKey: 'id', 
    pidKey: 'pid', 
    childrenKey: 'children',
    root: ROOT_ID,
    formatNode: node => node,
};

/**
 * 补全属性
 *  - parentKey: 父节点的key
 *  - isleaf: 是否是叶子节点
 *  - depth: 深度
 *  - path: 节点路径
 *  - dataIndex: 深度优先遍历时的index
 *  - leafIndex: 相对于叶子节点的index
 *  - height: 高度
 *  - childNodes: 后代节点
 *  - childLeafNodes: 后代叶子节点
 *  - range: 范围
 *  - TODO:prevLeafNode: 前一个叶子节点
 *  - TODO:nextLeafNode: 后一个叶子节点
 * @returns {{ tree, height, nodes, leafNodes, visibleNodes }}
 */
export function formatTree(tree, callback = node => node, config = {}) {
    let { idKey, pidKey, childrenKey, isLeafKey, root, expandedKeys, onlyVisible = false } = { ...DEFAULT_CONFIG, ...config };
    let leafIndex = 0;

    let result = dfs(tree, callback);
    return result;

    function dfs(tree, callback, param = {}) {
        let { 
            pid = root,
            baseDepth = 0,
            basePath = [],
            baseIndex = 0,
            dataIndex = 0,
            viewIndex = 0,
            parentVisible = true,
        } = param;
        let nodes = [], leafNodes = [], subHeight = 0, visibleNodes = [];

        tree = tree.map(node => {
            let isleaf = isLeafNode(node, { childrenKey, isLeafKey }), id = _getProperty(node, idKey);

            let nodeProperties = {
                [typeof pidKey === 'string' ? pidKey : 'pid']: pid,
                isleaf,
                depth: baseDepth,
                path: basePath.concat(id),
                dataIndex,
                leafIndex,
                viewIndex: parentVisible ? viewIndex : -1,
            };

            let isExpand = expandedKeys ? expandedKeys.has(id) : false;

            if (isleaf) {
                // 节点属性
                nodeProperties.prevLeafNode = null;
                nodeProperties.nextLeafNode = null;
                nodeProperties.height = 0;
                nodeProperties.range = [baseIndex, baseIndex + 1];

                // 迭代属性
                baseIndex++;
                dataIndex++;
                leafIndex++;
                parentVisible && viewIndex++;

                // 格式化节点
                node = callback({ ...node, ...nodeProperties });

                // 迭代结果
                nodes.push(node);
                leafNodes.push(node);
                parentVisible && visibleNodes.push(node);
            } else if(node[childrenKey]){
                let subResult = dfs(node[childrenKey], callback, { 
                    pid: id,
                    baseDepth: baseDepth + 1,
                    basePath: nodeProperties.path,
                    baseIndex,
                    dataIndex: dataIndex + 1,
                    parentVisible: parentVisible && isExpand,
                    viewIndex: viewIndex + 1,
                });

                node[childrenKey] = subResult.tree;

                // 树属性
                subHeight = Math.max(subHeight, subResult.height);
                
                // 节点属性
                nodeProperties.height = subResult.height;
                nodeProperties.childNodes = subResult.nodes;
                nodeProperties.childLeafNodes = subResult.leafNodes;
                nodeProperties.range = [baseIndex, baseIndex + subResult.leafNodes.length];

                // 迭代属性
                dataIndex += subResult.nodes.length + 1;
                baseIndex += subResult.leafNodes.length;

                // 格式化节点
                node = callback({ ...node, ...nodeProperties });

                // 迭代结果
                nodes = nodes.concat(node, subResult.nodes);
                leafNodes = leafNodes.concat(subResult.leafNodes);
                parentVisible && (visibleNodes = visibleNodes.concat(node, subResult.visibleNodes));
            }

            return node;
        });
        
        return { tree, height: subHeight + 1, nodes, leafNodes, visibleNodes };
    }
}

export function dfs(...rest) {
    let index = 0;
    
    function _dfs (tree, callback, config = {}) {
        let { childrenKey } = { ...DEFAULT_CONFIG, ...config };
    
        tree = tree.map(node => {
            node = { ...node };
            
            node = callback(node, index++ );
            
            if (Array.isArray(node[childrenKey])) {
                node[childrenKey] = _dfs(node[childrenKey], callback);
            } 
    
            return node;
        });
            
        return tree;
    }
    return _dfs(...rest);
}

export function isLeafNode(node, config = {}) {
    let { isLeafKey, childrenKey } = { ...DEFAULT_CONFIG, ...config };
    return node.hasOwnProperty(isLeafKey) ? node[isLeafKey] : !Array.isArray(node[childrenKey]);
}

export function createTree(flatData, config = {}) {
    let { idKey, pidKey, childrenKey, root } = { ...DEFAULT_CONFIG, ...config };

    let data = [], dataMap = {};
    flatData.forEach(node => {
        node = { ...node };
        delete node[childrenKey];
        dataMap[_getProperty(node, idKey)] = node;
        data.push(node);
    });

    return data.filter(node => {
        let parentNode = dataMap[_getProperty(node, pidKey)];
        if (parentNode) {
            if (!parentNode[childrenKey]) {
                parentNode[childrenKey] = [];
            }
            parentNode[childrenKey].push(node);
        } else {
            return _getProperty(node, pidKey) === root;
        }
    });
}

function _getProperty(row, property) {
    if (typeof property === 'function') {
        return property(row);
    } else {
        return row[property];
    }
}

